import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import VuiSelect from '../../../../../vodea/@vodea-ui/components/Forms/VuiSelect';
import _ from 'lodash';
import NumberFormat from 'react-number-format';
import { VuiButton } from '../../../../../vodea/@vodea-ui/components/VuiButton';
import { ProductInputs, productSchema } from '../../validation';
import { yupResolver } from '@hookform/resolvers/yup';
import StorageRepository from '../../../../../repositories/StorageRepository';
import UnitRepository from '../../../../../repositories/UnitRepository';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { ICartItem } from '../../index';
import { getBestMatchingItem } from '../../../../../vodea/utilities/helpers/global';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  selectedData: Partial<ICartItem>;
  customer: any;
  onSubmit: (data: any) => void;
}

const ModalCartItem: React.FC<Props> = ({isOpen, onClose, onSubmit, selectedData, customer}) => {
  const {t} = useTranslation();
  const {
    handleSubmit: cartItemHandleSubmit,
    control: cartItemControl,
    setValue: cartItemSetValue,
    errors: cartItemErrors,
    reset: cartItemReset,
    getValues: cartItemGetValues,
    watch: cartItemWatch,
  } = useForm<ProductInputs>({
    resolver: yupResolver(productSchema),
    defaultValues: {
      available_stock: 0,
      price: 0,
      total: 0,
      quantity: 0,
      discount: 0,
      unit_id: null,
      discount_product: 0,
      product_id: null,
      storage_id: null,
      total_discount: 0
    }
  });

  const [isInitialize, setIsInitialize] = useState(false);
  const watchProductId = cartItemWatch('product_id', null);
  const watchUnitId = cartItemWatch('unit_id', null);
  const watchQuantity = cartItemWatch('quantity', 0);
  const watchDiscount = cartItemWatch('discount', 0);
  const watchPrice = cartItemWatch('price', 0);
  const watchDiscountProduct = cartItemWatch('discount_product', 0);
  const [showWarningPrice, setShowWarningPrice] = useState(false)
  const comparePriceAndStock = useCallback(() => {
    setShowWarningPrice(false);
    const price = cartItemGetValues('price');
    const product = cartItemGetValues('product_id');
    const unit = cartItemGetValues('unit_id');
    const productConversion = _.get(product, 'product_conversions', []);
    const findUnit = productConversion?.find((product: any) => product.unit_id == _.get(unit, 'id'));

    if (findUnit) {
      if (price < _.get(product, 'fifo_price', 0) * parseInt(_.get(findUnit, 'quantity'))) {
        setShowWarningPrice(true);
      }
    } else {
      if (price < _.get(product, 'fifo_price', 0)) {
        setShowWarningPrice(true);
      }
    }
  }, [])

  const onItemSubmit = cartItemHandleSubmit((data) => {
    if (onSubmit) onSubmit(data);
    handleOnClose();
  });

  const getProductPrice = useCallback(() => {
    if (!watchQuantity || !watchUnitId) return;

    let productTiers = selectedData?.product_id?.product_tiers ?? [];
    if (!productTiers.length) return;
    let price = 0;
    productTiers = productTiers.filter((pt) => pt.unit_id == Number(_.get(watchUnitId, 'id')));
    const tier = productTiers.find((pt) => watchQuantity >= pt.quantity);
    if (tier && (tier?.product_tier_prices ?? []).length) {
      const customerMembershipId = customer?.membership_id;
      // Filter tier prices by customer membership_id
      const tierPrice = tier.product_tier_prices.find((ttp: any) => ttp.membership_id == customerMembershipId);
      price = tierPrice ? Number(tierPrice.price) : Number(tier.product_tier_prices[0].price);
    }
    cartItemSetValue('price', price);
  }, [watchQuantity, watchUnitId, selectedData, customer]);

  const handleOnClose = useCallback(() => {
    cartItemReset();
    onClose()
  }, []);

  const calculateTotal = useCallback(() => {
    const price = cartItemGetValues('price');
    const discount = cartItemGetValues('discount');
    const quantity = cartItemGetValues('quantity');
    const discountProduct = cartItemGetValues('discount_product');
    const totalDiscountProduct = quantity * discountProduct;

    const total = quantity * price - discount - totalDiscountProduct;
    cartItemSetValue('total_discount', totalDiscountProduct);
    cartItemSetValue('total', total);
  }, [selectedData]);

  const setDiscountProduct = useCallback(() => {
    const conversions = selectedData.product_id?.product_conversions || [];
    const conversion = conversions.find((c) => c.unit_id == _.get(watchUnitId, 'id'));
    let quantity = cartItemGetValues('quantity');
    if (conversion) {
      quantity = Number(quantity) * Number(conversion.quantity)
    }
    cartItemSetValue('discount_product', calculateDiscountProduct(quantity));
  }, [selectedData, watchUnitId]);

  const calculateDiscountProduct = useCallback((quantity: number) => {
    const productDiscounts = selectedData.product_id?.product_discounts || [];
    if (!productDiscounts.length || !quantity) return 0;
    const productDiscount = getBestMatchingItem(productDiscounts, quantity);
    return (productDiscount?.discount * 1) ?? 0;
  }, [selectedData]);

  useEffect(() => {
    if (isInitialize) {
      setDiscountProduct();
      getProductPrice();
    }
  }, [watchQuantity, watchUnitId]);

  useEffect(() => {
    if (isInitialize) {
      calculateTotal();
    }
  }, [
    watchDiscount,
    watchDiscountProduct,
    watchPrice,
    watchQuantity
  ]);

  useMemo(() => {
    if (selectedData && isOpen) {
      cartItemReset({
        product_id: selectedData?.product_id,
        unit_id: selectedData?.unit_id,
        storage_id: selectedData?.storage_id,
        discount_product: selectedData?.discount_product,
        discount: selectedData?.discount,
        quantity: selectedData?.quantity,
        total: selectedData?.total,
        price: selectedData.price,
        available_stock: selectedData?.available_stock
      });
      setIsInitialize(true);
    }
  }, [isOpen, selectedData]);

  return (
    <Modal show={isOpen} onHide={handleOnClose} backdrop={'static'} centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Cart</Modal.Title>
      </Modal.Header>

      <form className={'form-wrapper'} onSubmit={onItemSubmit}>
        <Modal.Body>
          <Controller
            name={'id'}
            control={cartItemControl}
            defaultValue={0}
            render={props => <input type={'hidden'} defaultValue={props.value} />}
          />
          <div
            className={clsx({
              'form-group': true,
              error: cartItemErrors.storage_id
            })}
          >
            <label className={'form-label'}>Storage</label>
            <Controller
              name={'storage_id'}
              control={cartItemControl}
              defaultValue={null}
              render={props => (
                <VuiSelect
                  propKey={_.get(watchProductId, 'id')}
                  selectParams={{
                    for: 'sales-order',
                    product: _.get(watchProductId, 'id')
                  }}
                  selectRepository={StorageRepository}
                  defaultValue={props.value}
                  onChange={val => {
                    props.onChange(val);
                  }}
                  isMulti={false}
                  isDisabled={!watchProductId}
                />
              )}
            />
            <label className={'label-help'}>{_.get(cartItemErrors.storage_id, 'message')}</label>
          </div>

          <div
            className={clsx({
              'form-group': true,
              error: cartItemErrors.unit_id
            })}
          >
            <label className={'form-label'}>Unit</label>
            <Controller
              name={'unit_id'}
              control={cartItemControl}
              render={props => (
                <VuiSelect
                  propKey={_.get(watchProductId, 'id')}
                  selectParams={{
                    for: 'sales-order',
                    product: _.get(watchProductId, 'id')
                  }}
                  selectRepository={UnitRepository}
                  defaultValue={props.value}
                  onChange={val => {
                    props.onChange(val);
                    getProductPrice();
                  }}
                  isDisabled={!watchProductId}
                />
              )}
            />
            <label className={'label-help'}>{_.get(cartItemErrors.unit_id, 'message')}</label>
          </div>

          <div className="row">
            <div className='col-md-6'>
              <div
                className={clsx({
                  'form-group': true,
                  error: cartItemErrors.available_stock
                })}
              >
                <label className={'form-label'}>Available Stock</label>
                <Controller
                  name={'available_stock'}
                  control={cartItemControl}
                  defaultValue={null}
                  render={props => (
                    <NumberFormat
                      className='form-control'
                      value={props.value}
                      allowNegative
                      thousandSeparator={'.'}
                      decimalSeparator={','}
                      decimalScale={0}
                      disabled
                      placeholder={'0'}
                      suffix={_.get(watchUnitId, 'id') ? ` ${_.get(watchUnitId, 'name')}` : ''}
                      onValueChange={({ value }) => {
                        props.onChange(value);
                      }}
                    />
                  )}
                />
                <label className={'label-help'}>{cartItemErrors.available_stock?.message}</label>
              </div>
            </div>
            <div className='col-md-6'>
              <div
                className={clsx({
                  'form-group': true,
                  error: cartItemErrors.quantity
                })}
              >
                <label className={'form-label'}>Quantity</label>
                <Controller
                  name={'quantity'}
                  control={cartItemControl}
                  render={props => (
                    <NumberFormat
                      className='form-control'
                      value={props.value}
                      allowNegative={false}
                      thousandSeparator={'.'}
                      decimalSeparator={','}
                      decimalScale={0}
                      placeholder={'0'}
                      onValueChange={({ value }) => {
                        props.onChange(value);
                        getProductPrice();
                      }}
                    />
                  )}
                />
                <label className={'label-help'}>{cartItemErrors.quantity?.message}</label>
              </div>
            </div>
          </div>

          <div
            className={clsx({
              'form-group': true,
              error: cartItemErrors.price
            })}
          >
            <label className={'form-label'}>Price / Unit</label>
            {showWarningPrice ? (
              <OverlayTrigger
                key={`table-error`}
                placement={'top'}
                overlay={<Tooltip id={`tooltip-table`}>Harga jual anda di bawah harga modal</Tooltip>}
              >
                <FontAwesomeIcon icon={faExclamationCircle} className='price-warning' />
              </OverlayTrigger>
            ) : (
              ''
            )}
            <Controller
              name={'price'}
              control={cartItemControl}
              defaultValue={null}
              render={props => (
                <NumberFormat
                  className='form-control'
                  value={props.value}
                  allowNegative={false}
                  thousandSeparator={'.'}
                  decimalSeparator={','}
                  decimalScale={0}
                  placeholder={'IDR 0'}
                  prefix={'IDR '}
                  onValueChange={({ value }) => {
                    props.onChange(value);
                    comparePriceAndStock();
                  }}
                />
              )}
            />
            <label className={'label-help'}>{cartItemErrors.price?.message}</label>
          </div>

          <div
            className={clsx({
              'form-group': true,
              error: cartItemErrors.discount
            })}
          >
            <label className={'form-label'}>Discount</label>
            <Controller
              name={'discount'}
              control={cartItemControl}
              defaultValue={null}
              render={props => (
                <NumberFormat
                  className='form-control'
                  value={props.value}
                  allowNegative={false}
                  thousandSeparator={'.'}
                  decimalSeparator={','}
                  decimalScale={0}
                  placeholder={'IDR 0'}
                  prefix={'IDR '}
                  onValueChange={({ value }) => props.onChange(value)}
                />
              )}
            />
            <label className={'label-help'}>{cartItemErrors.discount?.message}</label>
          </div>

          <div
            className={clsx({
              'form-group': true,
              error: cartItemErrors.discount_product
            })}
          >
            <label className={'form-label'}>Discount Product</label>
            <Controller
              name={'discount_product'}
              control={cartItemControl}
              defaultValue={0}
              render={props => (
                <NumberFormat
                  className='form-control'
                  value={props.value}
                  allowNegative={false}
                  thousandSeparator={'.'}
                  decimalSeparator={','}
                  decimalScale={0}
                  placeholder={'IDR 0'}
                  prefix={'IDR '}
                  onValueChange={({ value }) => props.onChange(value)}
                />
              )}
            />
            <label className={'label-help'}>{cartItemErrors.discount_product?.message}</label>
          </div>

          <div className='form-group'>
            <label className={'form-label'}>Total Discount Product</label>
            <Controller
              name={'total_discount'}
              control={cartItemControl}
              defaultValue={0}
              render={props => (
                <h4>
                  <NumberFormat
                    value={props.value}
                    displayType={'text'}
                    allowNegative={false}
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    decimalScale={0}
                    prefix={'IDR '}
                    placeholder={'IDR 0'}
                  />
                </h4>
              )}
            />
          </div>

          <div className='form-group'>
            <label className={'form-label'}>Total</label>
            <Controller
              name={'total'}
              control={cartItemControl}
              defaultValue={0}
              render={props => (
                <h4>
                  <NumberFormat
                    value={props.value}
                    displayType={'text'}
                    allowNegative={true}
                    thousandSeparator={'.'}
                    decimalSeparator={','}
                    decimalScale={0}
                    prefix={'IDR '}
                    placeholder={'IDR 0'}
                  />
                </h4>
              )}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant={'light'} onClick={onClose}>
            {t('Cancel')}
          </Button>
          <VuiButton forSubmit={true} label={'Save'} loading={false} />
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default ModalCartItem;